/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
// import { CSSTransition } from "react-transition-group";
import axios from "axios";
import Card from "components/card";
import InputField from "components/fields/InputField";
import DynamicInputField from "components/fields/DynamicInputField";
import { Select, Button } from "@chakra-ui/react";
import SweetAlert2 from "react-sweetalert2";
import { SubaccountContext } from "../../../store/SubaccountProvider";
import ReceiverSelectorModal from "./components/ReceiverSelectorModal";
import ReceiverAccountSelectorModal from "./components/ReceiverAccountSelectorModal";

interface PayloadType {
  [key: string]: any;
}

const CreatePayout: React.FC = () => {
  const { setSubaccountBalance, setIsGettingBalance } =
    useContext(SubaccountContext);
  const initialData: {
    currency: string;
    receiver_id: string;
    account_id: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    gender: string;
    email: string;
    dob: string;
    dialcode: string;
    phone: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    amount: number;
    reference: string;
    note: string;
  } = {
    currency: "",
    receiver_id: "",
    account_id: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    gender: "",
    email: "",
    dob: "",
    dialcode: "",
    phone: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    postcode: "",
    country: "",
    amount: 0,
    reference: "",
    note: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isIndicateTerms, setIsIndicateTerms] = useState<boolean>(false);
  const [isOpenReceiverDetailsForm, setIsOpenReceiverDetailsForm] =
    useState<boolean>(false);
  const [payout, setPayout] = useState<any>(initialData);
  const [swalProps, setSwalProps] = useState<any>({});
  const [receiverId, setReceiverId] = useState<string>("");
  const [receiverAccountId, setReceiverAccountId] = useState<string>("");
  const [assetList, setAssetList] = useState<any>([]);
  const [selectedReceiverAccount, setSelectedReceiverAccount] =
    useState<any>(null);
  const subaccountId = localStorage.getItem("subaccountId");
  const subaccountName = localStorage.getItem("subaccountName");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOpenReceiverSelectorModal, setIsOpenReceiverSelectorModal] =
    useState<boolean>(false);
  const [
    isOpenReceiverAccountSelectorModal,
    setIsOpenReceiverAccountSelectorModal,
  ] = useState<boolean>(false);

  // const receiverDetailsBoxRef = useRef(null);
  // const receiverAccountDetailsBoxRef = useRef(null);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  const navigate = useNavigate();

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  const fetchAccountDetail = async (signal?: AbortSignal) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountId}`,
        { signal }
      );

      const accountData = response.data.subaccount?.payload;

      if (!accountData) {
        throw new Error("No subaccount data found");
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            asset: method.asset,
            decimal: method.decimal ?? 2,
            crypto: method.crypto,
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      // Ignore cancelled requests
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      console.error("Error fetching subaccount details:", error);
    }
  };

  const checkSubaccountBalances = async (currency: string, amount: number) => {
    try {
      const queryObject: PayloadType = {
        currency: currency,
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-balance?${queryString}`
      );

      const balances = response.data?.balances;
      if (balances && balances.available && balances.available >= amount) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error get balance data", error);
      return false;
    }
  };

  const getSubaccountBalances = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/subaccount/get-subaccount-balance`
      );

      const balances = response.data?.balances;
      if (balances) {
        setSubaccountBalance(balances);
      }
    } catch (error) {
      console.error("Error get balance data", error);
      return false;
    }
  };

  // Helper functions for decimal validation
  const getSelectedAssetDecimal = () => {
    if (!payout.currency || assetList.length === 0) return 2;

    const selectedAsset = assetList.find(
      (asset: any) => asset.asset === payout.currency
    );
    return selectedAsset?.decimal ?? 2;
  };

  const getSelectedAssetCrypto = () => {
    if (!payout.currency || assetList.length === 0) return false;

    const selectedAsset = assetList.find(
      (asset: any) => asset.asset === payout.currency
    );
    return selectedAsset?.crypto ?? false;
  };

  const validateDecimalPlaces = (value: string, maxDecimals: number) => {
    if (!value.includes(".")) return true;
    const decimalPart = value.split(".")[1];
    return !decimalPart || decimalPart.length <= maxDecimals;
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow navigation and editing keys
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "Home",
        "End",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key)
    ) {
      return;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
      return;
    }

    // Allow only digits and decimal point
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (e.key === "." && payout.amount.toString().includes(".")) {
      e.preventDefault();
      return;
    }

    // Check decimal places limit when typing after decimal point
    if (payout.currency && payout.amount.toString().includes(".")) {
      const maxDecimals = getSelectedAssetDecimal();
      const currentValue = payout.amount.toString();
      const cursorPosition = (e.target as HTMLInputElement).selectionStart || 0;
      const decimalIndex = currentValue.indexOf(".");

      // If typing after decimal point and would exceed max decimals
      if (
        cursorPosition > decimalIndex &&
        currentValue.length - decimalIndex - 1 >= maxDecimals
      ) {
        // Only allow if we're replacing/selecting text
        const selectionLength =
          ((e.target as HTMLInputElement).selectionEnd || 0) - cursorPosition;
        if (selectionLength === 0) {
          e.preventDefault();
        }
      }
    }
  };

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;

    if (id === "indicate_terms") {
      setIsIndicateTerms(event.target.checked);
      return;
    }

    // Reset amount and other fields when currency changes
    if (id === "currency") {
      setPayout({
        ...payout,
        currency: value,
        amount: 0,
        reference: "",
        note: "",
      });
      setIsOpenReceiverDetailsForm(false);
      setSearchTerm("");
      setReceiverId("");
      setReceiverAccountId("");
      setSelectedReceiverAccount(null);
      return;
    }

    if (id === "amount" && payout.currency) {
      const maxDecimals = getSelectedAssetDecimal();

      if (!validateDecimalPlaces(value, maxDecimals)) {
        return;
      }
    }

    setPayout({ ...payout, [id]: value });
  };

  const resetReceiverSelected = () => {
    let dataToReset = JSON.parse(JSON.stringify(initialData));

    dataToReset.currency = payout.currency;
    dataToReset.amount = payout.amount;
    dataToReset.reference = payout.reference;
    dataToReset.note = payout.note;

    setPayout(dataToReset);
    setIsOpenReceiverDetailsForm(false);

    setSearchTerm("");
    setReceiverId("");

    setReceiverAccountId("");
    setSelectedReceiverAccount(null);
  };

  const isFormValid =
    payout !== null
      ? payout.currency &&
        payout.receiver_id &&
        payout.account_id &&
        payout.first_name &&
        payout.last_name &&
        payout.email &&
        payout.dob &&
        payout.dialcode &&
        payout.phone &&
        payout.address &&
        payout.city &&
        payout.postcode &&
        payout.country &&
        payout.amount > 0 &&
        isIndicateTerms
      : false;

  const handleSubmit = async () => {
    setIsCreating(true);
    let isFormReady = true;
    const username = localStorage.getItem("username");
    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    const amount = parseFloat(payout.amount);

    if (!isIndicateTerms && isFormReady) {
      isFormReady = false;
      setIsCreating(false);
      setSwalProps({
        show: true,
        icon: "warning",
        title: "Action Required",
        html: "Kindly indicate your acceptance of the terms and conditions to continue.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
    }

    if (isFormReady) {
      const sufficientBalance = await checkSubaccountBalances(
        payout.currency,
        amount
      );
      if (!sufficientBalance) {
        isFormReady = false;
        setIsCreating(false);
        setSwalProps({
          show: true,
          icon: "warning",
          title: "Attention!",
          html: "Insufficient Balance to process payout",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
          },
        });
      }
    }

    if (isFormReady) {
      payout.subaccount_id = subaccountId;
      payout.userRole = userRole;
      payout.username = username;

      if (payout.state === "") {
        delete payout.state;
      }

      payout.crypto = getSelectedAssetCrypto();

      try {
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/payout/action/create-payout`,
            payout
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Create payout successfully",
                showConfirmButton: true,
                didClose: async () => {
                  setIsGettingBalance(true);
                  await getSubaccountBalances();
                  setSwalProps({});
                  setTimeout(() => {
                    setIsGettingBalance(false);
                    navigate(`/${userRole}/payouts`);
                  }, 2000);
                },
              });
            } else {
              setIsCreating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsCreating(false);
                },
              });
            }
          })
          .catch(function (error) {
            let errorMessage = "Create payout has been failed";
            if (
              error.response &&
              (error.response.status === 400 || error.response.status === 500)
            ) {
              errorMessage = error.response.data.message;
            }

            setIsCreating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: errorMessage,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsCreating(false);
              },
            });
          });
      } catch (error: any) {
        setIsCreating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
          },
        });
      }
    }
  };

  const handleReceiverSelect = async (receiverData: any) => {
    if (receiverData) {
      const receiverDetails = {
        receiver_id: receiverData.id,
        first_name: receiverData.first_name,
        last_name: receiverData.last_name,
        email: receiverData.email,
        middle_name: receiverData.middle_name,
        gender: receiverData.gender,
        dob: receiverData.dob,
        dialcode: receiverData.dialcode,
        phone: receiverData.phone,
        address: receiverData.address,
        address2: receiverData.address2,
        city: receiverData.city,
        state: receiverData.state,
        postcode: receiverData.postcode,
        country: receiverData.country,
      };
      const displayName = `${receiverData.first_name} ${receiverData.last_name} (${receiverData.email})`;
      setSearchTerm(truncateString(displayName, 80));
      setReceiverId(receiverData.id);
      setPayout({ ...payout, ...receiverDetails });
      setIsOpenReceiverDetailsForm(true);
      // Reset account selection when receiver changes
      setReceiverAccountId("");
      setSelectedReceiverAccount(null);
    }
  };

  const handleReceiverAccountSelect = (accountData: any) => {
    if (accountData) {
      setReceiverAccountId(accountData.id);
      setPayout({ ...payout, account_id: accountData.id });
      setSelectedReceiverAccount(accountData);
    }
  };

  const paymentDetailsFieldsRendering = () => {
    if (!selectedReceiverAccount?.method_details) return null;

    const arrayOfInfo = Object.entries(
      selectedReceiverAccount.method_details
    ).map(([key, value]) => ({
      name: key,
      value: value,
    }));

    const renderInput = (field: any) => {
      const capitalizeLabel = (label: any) => {
        let modifiedLabel = label
          .split("_")
          .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        switch (label) {
          case "bsb":
            modifiedLabel = "BSB";
            break;
          case "bic":
            modifiedLabel = "BIC";
            break;
          case "iban":
            modifiedLabel = "IBAN";
            break;
        }

        return modifiedLabel;
      };

      return (
        <DynamicInputField
          key={field.name}
          disabled={isLoading}
          readonly={true}
          variant="auth"
          extra="mb-3 shadow-sm"
          label={capitalizeLabel(field.name)}
          id={field.name}
          name={field.name}
          value={field.value}
          type="text"
          required={false}
          autoComplete="off"
        />
      );
    };

    return arrayOfInfo.map(renderInput);
  };

  const formatLabel = (label: any) => {
    let modifiedLabel = label.split("_").join(" ");
    switch (label) {
      case "bsb":
        modifiedLabel = "BSB";
        break;
      case "bic":
        modifiedLabel = "BIC";
        break;
      case "iban":
        modifiedLabel = "IBAN";
        break;
    }

    return modifiedLabel;
  };

  const truncateString = (str: string, maxLength: number): string => {
    return str.length > maxLength ? str.slice(0, maxLength - 3) + "..." : str;
  };

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      await fetchAccountDetail(abortController.signal);
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    // <form>
    //   <div className="mb-5">
    //     <div className="flex items-center justify-between gap-4">
    //       <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
    //         Create Payout
    //       </h3>

    //       <div className="hidden items-center gap-2 text-xs font-medium md:flex">
    //         <span
    //           className={payout.currency ? "text-green-600" : "text-gray-400"}
    //         >
    //           Asset
    //         </span>
    //         <span className="text-gray-300">→</span>
    //         <span className={receiverId ? "text-green-600" : "text-gray-400"}>
    //           Receiver
    //         </span>
    //         <span className="text-gray-300">→</span>
    //         <span
    //           className={payout.amount ? "text-green-600" : "text-gray-400"}
    //         >
    //           Amount
    //         </span>
    //         <span className="text-gray-300">→</span>
    //         <span
    //           className={receiverAccountId ? "text-green-600" : "text-gray-400"}
    //         >
    //           Account
    //         </span>
    //       </div>
    //     </div>
    //   </div>

    //   <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm transition-all duration-200">
    //     <div>
    //       <div className="mb-4 flex items-center justify-between">
    //         <h3 className="text-base font-semibold text-navy-700 dark:text-white">
    //           Payment Asset
    //         </h3>
    //         {payout.currency ? (
    //           <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
    //             Selected
    //           </span>
    //         ) : null}
    //       </div>

    //       <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
    //         Asset <span className="text-red-500">*</span>
    //       </label>

    //       <Select
    //         disabled={isCreating || isLoading}
    //         id="currency"
    //         value={payout.currency}
    //         onChange={handleInputChange}
    //         className="text-primary h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm shadow-sm outline-none transition focus:border-navy-400 focus:ring-2 focus:ring-navy-200"
    //       >
    //         <option value="">
    //           {isLoading ? "Retrieving available..." : "-- Please Select --"}
    //         </option>

    //         {assetList?.filter((item: any) => !item.crypto).length > 0 && (
    //           <optgroup label="Fiat">
    //             {assetList
    //               .filter((item: any) => !item.crypto)
    //               .map((item: any) => (
    //                 <option
    //                   key={item.asset}
    //                   value={item.asset}
    //                   data-decimal={item.decimal}
    //                 >
    //                   {item.asset}
    //                 </option>
    //               ))}
    //           </optgroup>
    //         )}

    //         {assetList?.filter((item: any) => item.crypto).length > 0 && (
    //           <optgroup label="Crypto">
    //             {assetList
    //               .filter((item: any) => item.crypto)
    //               .map((item: any) => (
    //                 <option
    //                   key={item.asset}
    //                   value={item.asset}
    //                   data-decimal={item.decimal}
    //                 >
    //                   {item.asset}
    //                 </option>
    //               ))}
    //           </optgroup>
    //         )}
    //       </Select>

    //       {!payout.currency ? (
    //         <p className="mt-2 text-sm text-gray-500 dark:text-white/60">
    //           Select an asset to unlock receiver selection.
    //         </p>
    //       ) : null}
    //     </div>

    //     <div>
    //       <div className="mb-4 flex items-center justify-between">
    //         <h3 className="text-base font-semibold text-navy-700 dark:text-white">
    //           Payee Details
    //         </h3>
    //         {receiverId ? (
    //           <Button
    //             type="button"
    //             onClick={() => {
    //               if (!isCreating) {
    //                 setSearchTerm("");
    //                 resetReceiverSelected();
    //               }
    //             }}
    //             disabled={isCreating}
    //             className="flex items-center gap-1 text-sm font-medium tracking-tight text-blue-600 transition hover:text-blue-700 disabled:opacity-50"
    //           >
    //             Change account
    //           </Button>
    //         ) : null}
    //       </div>

    //       {payout.currency === "" ? (
    //         <p className="text-sm text-gray-500 dark:text-white/60">
    //           Please select a payment asset before choosing the payee.
    //         </p>
    //       ) : (
    //         <div className="relative w-full">
    //           {receiverId === "" ? (
    //             <Button
    //               type="button"
    //               onClick={() => setIsOpenReceiverSelectorModal(true)}
    //               disabled={isCreating}
    //               className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    //             >
    //               <span className="text-gray-500">
    //                 Search and select a receiver
    //               </span>
    //               <span className="text-xs text-gray-400">Select</span>
    //             </Button>
    //           ) : (
    //             <div className="flex items-center gap-2">
    //               <div className="flex h-10 flex-1 items-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900 shadow-sm">
    //                 {searchTerm}
    //               </div>
    //             </div>
    //           )}
    //         </div>
    //       )}

    //       <CSSTransition
    //         in={isOpenReceiverDetailsForm}
    //         timeout={300}
    //         classNames="slide-down"
    //         unmountOnExit
    //         nodeRef={receiverDetailsBoxRef}
    //       >
    //         <div ref={receiverDetailsBoxRef} className="m-4 mt-8">
    //           <h4 className="mb-4 text-sm font-semibold text-navy-700 dark:text-white">
    //             Receiver Details
    //           </h4>

    //           <div className="space-y-2 text-sm">
    //             {[
    //               ["First name", payout.first_name],
    //               ["Middle name", payout.middle_name || "-"],
    //               ["Last name", payout.last_name],
    //               [
    //                 "Gender",
    //                 payout.gender
    //                   ? payout.gender === "M"
    //                     ? "Male"
    //                     : "Female"
    //                   : "-",
    //               ],
    //               ["Email", payout.email],
    //               ["DOB", payout.dob],
    //               [
    //                 "Phone",
    //                 payout.phone ? `${payout.dialcode} ${payout.phone}` : "-",
    //               ],
    //               [
    //                 "Address",
    //                 payout.address
    //                   ? `${payout.address}${
    //                       payout.address2 ? `, ${payout.address2}` : ""
    //                     }`
    //                   : "-",
    //               ],
    //               ["City", payout.city],
    //               ["State", payout.state || "-"],
    //               ["Postcode", payout.postcode],
    //               ["Country", payout.country],
    //             ].map(([label, value]) => (
    //               <div key={label} className="flex">
    //                 <div className="w-32 shrink-0 text-gray-500">{label}</div>
    //                 <div className="font-medium text-gray-900">{value}</div>
    //               </div>
    //             ))}
    //           </div>
    //         </div>
    //       </CSSTransition>
    //     </div>

    //     <div>
    //       <div className="mb-4 flex items-center justify-between">
    //         <h3 className="text-base font-semibold text-navy-700 dark:text-white">
    //           Payment Details
    //         </h3>

    //         {receiverAccountId && (
    //           <button
    //             type="button"
    //             onClick={() => {
    //               if (!isCreating) {
    //                 setReceiverAccountId("");
    //                 setPayout({ ...payout, account_id: "" });
    //                 setSelectedReceiverAccount(null);
    //               }
    //             }}
    //             disabled={isCreating}
    //             className="flex items-center gap-1 text-sm font-medium tracking-tight text-blue-600 transition hover:text-blue-700 disabled:opacity-50"
    //           >
    //             Change account
    //           </button>
    //         )}
    //       </div>

    //       {receiverId === "" ? (
    //         <p className="text-sm text-gray-500 dark:text-white/60">
    //           Please select a receiver to view payment account options.
    //         </p>
    //       ) : receiverAccountId === "" ? (
    //         <>
    //           <Button
    //             type="button"
    //             onClick={() => setIsOpenReceiverAccountSelectorModal(true)}
    //             disabled={isCreating}
    //             className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    //           >
    //             <span className="text-gray-500">
    //               Select a receiver payment account
    //             </span>
    //             <span className="text-xs text-gray-400">Select</span>
    //           </Button>

    //           <p className="mt-2 text-sm text-gray-500 dark:text-white/60">
    //             A payee account must be selected before you can proceed.
    //           </p>
    //         </>
    //       ) : null}

    //       <CSSTransition
    //         in={receiverAccountId !== "" && payout.currency !== ""}
    //         timeout={300}
    //         classNames="slide-down"
    //         unmountOnExit
    //         nodeRef={receiverAccountDetailsBoxRef}
    //       >
    //         <div ref={receiverAccountDetailsBoxRef} className="mt-3">
    //           {receiverAccountId !== "" && payout.currency !== "" && (
    //             <div>{paymentDetailsFieldsRendering()}</div>
    //           )}
    //         </div>
    //       </CSSTransition>
    //     </div>

    //     <div>
    //       <div className="mb-4 flex items-center justify-between">
    //         <h3 className="text-base font-semibold text-navy-700 dark:text-white">
    //           Payment Instruction
    //         </h3>

    //         {instructionDone && (
    //           <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
    //             Completed
    //           </span>
    //         )}
    //       </div>

    //       <InputField
    //         disabled={isCreating || payout.currency === ""}
    //         variant="auth"
    //         extra="mb-3 shadow-sm number-input"
    //         label="Amount"
    //         required={true}
    //         placeholder="e.g. 1,250.00"
    //         id="amount"
    //         type="number"
    //         autoComplete="off"
    //         value={payout.amount || ""}
    //         onChange={handleInputChange}
    //         onKeyDown={handleAmountKeyDown}
    //       />

    //       <InputField
    //         disabled={isCreating || payout.currency === ""}
    //         variant="auth"
    //         extra="mb-3 shadow-sm"
    //         label="Reference"
    //         placeholder="Enter reference (optional)"
    //         id="reference"
    //         type="text"
    //         autoComplete="off"
    //         value={payout.reference}
    //         onChange={handleInputChange}
    //       />

    //       <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
    //         Note
    //       </label>

    //       <textarea
    //         disabled={isCreating || payout.currency === ""}
    //         className="text-primary block h-20 w-full resize-none overflow-auto rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium tracking-tight shadow-sm outline-none transition focus:border-navy-400 focus:ring-2 focus:ring-navy-200 disabled:cursor-not-allowed disabled:opacity-50"
    //         id="note"
    //         placeholder="Enter note (optional)"
    //         autoComplete="off"
    //         value={payout.note}
    //         onChange={handleInputChange}
    //       />
    //     </div>
    //     {/* Confirm */}
    //     <div className="mt-5">
    //       <label className="flex cursor-pointer items-center gap-3">
    //         <input
    //           disabled={isCreating}
    //           id="indicate_terms"
    //           type="checkbox"
    //           className="h-3 w-3 rounded border-gray-300 text-navy-600"
    //           onChange={handleInputChange}
    //         />
    //         <span className="text-xs text-navy-700 dark:text-white">
    //           On behalf of <strong>{subaccountName}</strong>, I confirm the
    //           payout details entered above are correct.
    //         </span>
    //       </label>
    //     </div>

    //     {/* CTA */}
    //     <div className="flex items-center justify-end border-t border-gray-100 pt-4">
    //       <Button
    //         type="button"
    //         onClick={() => handleSubmit()}
    //         isDisabled={!isFormValid}
    //         isLoading={isCreating}
    //         loadingText="Processing..."
    //         className="btn-primary inline-flex h-10 items-center justify-center rounded-lg px-6 text-sm font-semibold shadow-sm disabled:opacity-60"
    //       >
    //         <span className="relative z-10 tracking-tight">Create payout</span>
    //         <span className="absolute inset-0 z-0 scale-0 rounded-lg bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
    //       </Button>
    //     </div>
    //   </div>

    //   <SweetAlert2
    //     {...swalProps}
    //     customClass={{
    //       actions: "custom-swal2-popup-action",
    //       title: "custom-swal2-popup-title",
    //       confirmButton:
    //         "custom-swal2-popup-action custom-swal2-popup-action-confirm",
    //       cancelButton:
    //         "custom-swal2-popup-action custom-swal2-popup-action-cancel",
    //     }}
    //   />

    //   {isOpenReceiverSelectorModal && (
    //     <ReceiverSelectorModal
    //       isOpen={isOpenReceiverSelectorModal}
    //       onClose={() => setIsOpenReceiverSelectorModal(false)}
    //       onSelect={handleReceiverSelect}
    //       currency={payout.currency}
    //     />
    //   )}

    //   {isOpenReceiverAccountSelectorModal && (
    //     <ReceiverAccountSelectorModal
    //       isOpen={isOpenReceiverAccountSelectorModal}
    //       onClose={() => setIsOpenReceiverAccountSelectorModal(false)}
    //       onSelect={handleReceiverAccountSelect}
    //       receiverId={receiverId}
    //       currency={payout.currency}
    //     />
    //   )}
    // </form>

    <form>
      <div className="mb-3 flex items-center justify-between gap-4">
        <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
          Create Payout
        </h3>

        <div className="hidden items-center gap-2 rounded-3xl border border-gray-200 bg-white px-6 py-3 text-xs font-medium md:flex">
          <span
            className={payout.currency ? "text-primary-600" : "text-gray-400"}
          >
            Asset
          </span>
          <span className="text-gray-300">→</span>
          <span className={receiverId ? "text-primary-600" : "text-gray-400"}>
            Receiver
          </span>
          <span className="text-gray-300">→</span>
          <span
            className={receiverAccountId ? "text-primary-600" : "text-gray-400"}
          >
            Account
          </span>
          <span className="text-gray-300">→</span>
          <span className={payout.amount ? "text-primary-600" : "text-gray-400"}>
            Amount
          </span>
        </div>
      </div>
      <Card className="rounded-xl border border-gray-200 bg-white p-10 shadow-sm">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* LEFT */}
          <div className="space-y-8 lg:col-span-6">
            {/* Payment Asset */}
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-md font-semibold text-navy-700 dark:text-white">
                  Payment Asset
                </h3>
                {payout.currency ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                    Selected
                  </span>
                ) : null}
              </div>

              <label className="block text-sm font-medium text-navy-700 dark:text-white">
                Asset <span className="text-red-500">*</span>
              </label>

              <Select
                disabled={isCreating || isLoading}
                id="currency"
                value={payout.currency}
                onChange={handleInputChange}
                className="dark:bg-neutral-900 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm shadow-sm outline-none transition focus:border-navy-400 focus:ring-2 focus:ring-navy-200 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white"
              >
                <option value="">
                  {isLoading
                    ? "Retrieving available..."
                    : "-- Please Select --"}
                </option>

                {assetList?.filter((item: any) => !item.crypto).length > 0 && (
                  <optgroup label="Fiat">
                    {assetList
                      .filter((item: any) => !item.crypto)
                      .map((item: any) => (
                        <option
                          key={item.asset}
                          value={item.asset}
                          data-decimal={item.decimal}
                        >
                          {item.asset}
                        </option>
                      ))}
                  </optgroup>
                )}

                {assetList?.filter((item: any) => item.crypto).length > 0 && (
                  <optgroup label="Crypto">
                    {assetList
                      .filter((item: any) => item.crypto)
                      .map((item: any) => (
                        <option
                          key={item.asset}
                          value={item.asset}
                          data-decimal={item.decimal}
                        >
                          {item.asset}
                        </option>
                      ))}
                  </optgroup>
                )}
              </Select>

              {!payout.currency ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-white/60">
                  Select an asset to unlock receiver selection.
                </p>
              ) : null}
            </section>

            {/* Receiver */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-md font-semibold text-navy-700 dark:text-white">
                  Receiver
                </h3>

                {receiverId ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isCreating) {
                        setSearchTerm("");
                        resetReceiverSelected();
                      }
                    }}
                    disabled={isCreating}
                    className="text-xs font-semibold text-blue-600 underline transition hover:text-blue-700 disabled:opacity-50"
                  >
                    Change
                  </button>
                ) : null}
              </div>

              {payout.currency === "" ? (
                <p className="text-sm text-gray-500 dark:text-white/60">
                  Please select a payment asset before choosing the receiver.
                </p>
              ) : receiverId === "" ? (
                <Button
                  type="button"
                  onClick={() => setIsOpenReceiverSelectorModal(true)}
                  disabled={isCreating}
                  className="dark:bg-neutral-900 flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white"
                >
                  <span className="text-navy-700 dark:text-white/60">
                    Search and select a receiver
                  </span>
                </Button>
              ) : (
                <div className="flex h-10 items-center rounded-lg border border-gray-300 bg-gray-50 px-4 text-sm font-medium text-gray-900 dark:border-white/10 dark:bg-white/5 dark:text-white">
                  {searchTerm}
                </div>
              )}

              {receiverId ? (
                <details className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-white/10 dark:bg-white/5">
                  <summary className="cursor-pointer text-sm font-semibold text-navy-700 dark:text-white">
                    Receiver details
                  </summary>

                  <div className="mt-4 space-y-2 text-sm">
                    {[
                      ["First name", payout.first_name],
                      ["Middle name", payout.middle_name || "-"],
                      ["Last name", payout.last_name],
                      [
                        "Gender",
                        payout.gender
                          ? payout.gender === "M"
                            ? "Male"
                            : "Female"
                          : "-",
                      ],
                      ["Email", payout.email],
                      ["DOB", payout.dob],
                      [
                        "Phone",
                        payout.phone
                          ? `${payout.dialcode} ${payout.phone}`
                          : "-",
                      ],
                      [
                        "Address",
                        payout.address
                          ? `${payout.address}${
                              payout.address2 ? `, ${payout.address2}` : ""
                            }`
                          : "-",
                      ],
                      ["City", payout.city],
                      ["State", payout.state || "-"],
                      ["Postcode", payout.postcode],
                      ["Country", payout.country],
                    ].map(([label, value]) => (
                      <div key={label} className="flex">
                        <div className="w-32 shrink-0 text-gray-500 dark:text-white/60">
                          {label}
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}
            </section>

            {/* Receiver Account */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-md font-semibold text-navy-700 dark:text-white">
                  Receiver Account
                </h3>

                {receiverAccountId ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isCreating) {
                        setReceiverAccountId("");
                        setPayout({ ...payout, account_id: "" });
                        setSelectedReceiverAccount(null);
                      }
                    }}
                    disabled={isCreating}
                    className="text-xs font-semibold text-blue-600 underline transition hover:text-blue-700 disabled:opacity-50"
                  >
                    Change
                  </button>
                ) : null}
              </div>

              {receiverId === "" ? (
                <p className="text-sm text-gray-500 dark:text-white/60">
                  Please select a receiver to view available accounts.
                </p>
              ) : receiverAccountId === "" ? (
                <Button
                  type="button"
                  onClick={() => setIsOpenReceiverAccountSelectorModal(true)}
                  disabled={isCreating}
                  className="dark:bg-neutral-900 flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white"
                >
                  <span className="text-navy-700 dark:text-white/60">
                    Select a receiver payment account
                  </span>
                </Button>
              ) : null}

              {receiverAccountId !== "" && payout.currency !== "" ? (
                <div className="mt-3">{paymentDetailsFieldsRendering()}</div>
              ) : null}
            </section>
          </div>

          {/* RIGHT */}
          <div className="space-y-4 lg:col-span-6">
            {/* Payment Instruction */}
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-md font-semibold text-navy-700 dark:text-white">
                  Payment Instruction
                </h3>
                {payout.amount ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                    Completed
                  </span>
                ) : null}
              </div>

              <InputField
                disabled={!receiverAccountId || isCreating}
                variant="auth"
                extra="mb-3 shadow-sm number-input"
                label="Amount"
                required={true}
                placeholder=""
                id="amount"
                type="number"
                autoComplete="off"
                value={payout.amount || ""}
                onChange={handleInputChange}
                onKeyDown={handleAmountKeyDown}
              />

              <InputField
                disabled={!receiverAccountId || isCreating}
                variant="auth"
                extra="mb-3 shadow-sm"
                label="Reference"
                placeholder="Enter reference (optional)"
                id="reference"
                type="text"
                autoComplete="off"
                value={payout.reference || ""}
                onChange={handleInputChange}
              />

              <label className="block text-sm font-medium text-navy-700 dark:text-white">
                Note
              </label>

              <textarea
                disabled={!receiverAccountId || isCreating}
                className="dark:bg-neutral-900 h-24 w-full resize-none overflow-auto rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm outline-none transition focus:border-navy-400 focus:ring-2 focus:ring-navy-200 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white"
                id="note"
                placeholder="Enter note (optional)"
                autoComplete="off"
                value={payout.note}
                onChange={handleInputChange}
              />
            </section>

            {/* Confirm */}
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  disabled={isCreating}
                  id="indicate_terms"
                  type="checkbox"
                  className="h-3 w-3 rounded border-gray-300 text-navy-600"
                  onChange={handleInputChange}
                />
                <span className="text-xs text-navy-700 dark:text-white">
                  On behalf of <strong>{subaccountName}</strong>, I confirm the
                  payout details entered above are correct.
                </span>
              </label>
            </section>
          </div>
        </div>

        <hr className="my-6 border-gray-200 dark:border-white/10" />

        {/* CTA */}
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => handleSubmit()}
            isDisabled={!isFormValid}
            isLoading={isCreating}
            loadingText="Processing..."
            className="btn-primary inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 active:translate-y-[1px] disabled:opacity-60 md:px-5"
          >
            Create Payout
          </Button>
        </div>
      </Card>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      />

      {isOpenReceiverSelectorModal && (
        <ReceiverSelectorModal
          isOpen={isOpenReceiverSelectorModal}
          onClose={() => setIsOpenReceiverSelectorModal(false)}
          onSelect={handleReceiverSelect}
          currency={payout.currency}
        />
      )}

      {isOpenReceiverAccountSelectorModal && (
        <ReceiverAccountSelectorModal
          isOpen={isOpenReceiverAccountSelectorModal}
          onClose={() => setIsOpenReceiverAccountSelectorModal(false)}
          onSelect={handleReceiverAccountSelect}
          receiverId={receiverId}
          currency={payout.currency}
        />
      )}
    </form>
  );
};

export default CreatePayout;
